/* ==========================================================================
   Theme-wide scroll reveal
   --------------------------------------------------------------------------
   Pairs with assets/mp-animate.css. That file pre-hides content (opacity only,
   never a transform). This file decides which of it is safe to animate,
   animates those on scroll, and instantly reveals everything else.

   THE CONTRACT
   Every element the stylesheet hides must end up with .mp-in, whatever this
   script decides about it. Anything missed stays invisible, so every skip path
   reveals rather than returns, and enforce() sweeps up afterwards in case the
   two selector lists ever drift apart.

   WHAT IS SKIPPED, AND WHY
   position:fixed elements, and anything containing one: a transform on an
   ancestor becomes their containing block, which moves drawers and overlays to
   the wrong place. Sticky is NOT skipped — a transform does not break sticky
   the way it breaks fixed, and the transform is removed again once the reveal
   finishes, so a sticky column animates in and then behaves normally.
   Carousel and slider innards: they live in overflow:hidden tracks that may
   never intersect the viewport, so an observer would leave them hidden.
   Sections that already animate themselves (the hero, brand story): running
   two systems over the same element makes both look wrong.
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;

  // Tells the head script's failsafe that the reveal system is alive, so it
  // does not strip the pre-hidden state out from under us.
  html.setAttribute('data-mp-ready', '');

  var cfg = window.mpAnimateConfig || {};
  var STAGGER_GRID = cfg.staggerGrid !== false;
  var MAX_STAGGER = 10; // past this the tail of a long list just feels slow
  var MAX_GRID_CHILDREN = 24;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Must match the hiding selectors in mp-animate.css. enforce() uses this to
     guarantee nothing the stylesheet hid is left behind. */
  var HIDDEN_SELECTOR =
    '.shopify-section .container > *,' +
    '.shopify-section:not(:has(.container)) > *:not([aria-hidden="true"]) > *';

  /* Mirrors SECTION_DENY in mp-animate.css — edit the two together. */
  var SECTION_DENY = /header|menu|announcement|footer|drawer|popup|modal|preloader|music|cursor|predictive|search|background|timezone|clock|logo|new-hero|brand-story/i;

  /* Containers whose children are positioned by something other than normal
     flow and must not be moved or hidden independently. */
  var UNSAFE_ANCESTOR =
    '[data-nhs],[class*="slider"],[class*="Slider"],[class*="carousel"],' +
    '[class*="swiper"],[class*="slick"],[class*="marquee"],[class*="drawer"],' +
    '[class*="modal"],[class*="popup"],dialog';

  /* Things that break when an ancestor is transformed. */
  var UNSAFE_DESCENDANT =
    '[class*="drawer"],[class*="modal"],[class*="popup"],[data-nhs],dialog';

  /* Background and decoration layers, which are never content. */
  var DECORATIVE = /(^|[\s_-])(bg|scrim|glow|overlay|backdrop|decoration|deco)([\s_-]|$)|__bg|__scrim|__glow|__overlay/i;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function safeQuery(root, selector) {
    // :has() throws on older engines rather than returning nothing.
    try {
      return root.querySelectorAll(selector);
    } catch (e) {
      return [];
    }
  }

  function revealNow(el) {
    el.classList.add('mp-in-now', 'mp-in');
    el.removeAttribute('data-mp-reveal');
  }

  function isFixed(el) {
    return window.getComputedStyle(el).position === 'fixed';
  }

  // Walks up only as far as the section, so cost stays bounded however deeply
  // the markup nests.
  function hasFixedAncestor(el, stop) {
    var node = el;
    while (node && node !== stop) {
      if (isFixed(node)) return true;
      node = node.parentElement;
    }
    return false;
  }

  function sectionIsDenied(section) {
    if (SECTION_DENY.test(section.id || '')) return true;
    if (SECTION_DENY.test(section.className || '')) return true;
    // Sections running their own entrance animation.
    if (section.querySelector('[data-nhs],[class$="__reveal"],[class*="__reveal "]')) return true;
    if (isFixed(section)) return true;
    return false;
  }

  function elementIsDenied(el, section) {
    if (el.hasAttribute('data-mp-no-anim')) return true;
    if (el.closest(UNSAFE_ANCESTOR)) return true;
    if (el.querySelector(UNSAFE_DESCENDANT)) return true;
    if (hasFixedAncestor(el, section)) return true;

    var cs = window.getComputedStyle(el);
    // Not rendered right now — an observer would never fire for it, and it may
    // be a closed accordion or an inactive tab panel.
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    return false;
  }

  /* Sections written before the .container convention still need a scope. The
     first child that is not a background layer is the content wrapper. */
  function fallbackScope(section) {
    var kids = section.children;
    for (var i = 0; i < kids.length; i += 1) {
      var el = kids[i];
      if (el.getAttribute('aria-hidden') === 'true') continue;
      if (DECORATIVE.test(el.className || '')) continue;
      var tag = el.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEMPLATE' || tag === 'NOSCRIPT') continue;
      return el;
    }
    return null;
  }

  function isStaggerableGrid(el) {
    if (!STAGGER_GRID) return false;
    var kids = el.children;
    if (kids.length < 2 || kids.length > MAX_GRID_CHILDREN) return false;
    var display = window.getComputedStyle(el).display;
    return display === 'grid' || display === 'flex' ||
           display === 'inline-grid' || display === 'inline-flex';
  }

  var observer = null;

  function getObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target); // reveal once, not on every pass
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
    );
    return observer;
  }

  function show(el) {
    el.classList.add('mp-in');

    // Hand the element back exactly as it was found: no transform, no
    // will-change, no lingering containing block for anything inside it.
    var done = function () {
      el.removeAttribute('data-mp-reveal');
      el.style.removeProperty('--mp-i');
      el.style.removeProperty('will-change');
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    // transitionend does not fire if the transition is interrupted or never
    // starts, so clean up on a timer as well.
    window.setTimeout(done, 3200);
  }

  function stage(el, index) {
    el.setAttribute('data-mp-reveal', '');
    el.style.setProperty('--mp-i', String(Math.min(index, MAX_STAGGER)));
    getObserver().observe(el);
  }

  function handled(el) {
    return el.classList.contains('mp-in') || el.hasAttribute('data-mp-reveal');
  }

  function scanSection(section) {
    var scopes = [];
    var containers = section.querySelectorAll('.container');

    if (containers.length) {
      each(containers, function (c) { scopes.push(c); });
    } else {
      var fb = fallbackScope(section);
      if (fb) scopes.push(fb);
    }
    if (!scopes.length) return;

    var candidates = [];
    scopes.forEach(function (scope) {
      each(scope.children, function (child) { candidates.push(child); });
    });
    if (!candidates.length) return;

    // A denied section still has to be revealed — the stylesheet hid these by
    // selector, before this script had any say.
    if (sectionIsDenied(section)) {
      candidates.forEach(revealNow);
      return;
    }

    var i = 0;
    candidates.forEach(function (el) {
      if (handled(el)) return;

      if (elementIsDenied(el, section)) {
        revealNow(el);
        return;
      }

      // A grid of cards reads better as a cascade than as one block, so the
      // children take over the reveal and the wrapper simply shows.
      if (isStaggerableGrid(el)) {
        el.classList.add('mp-in-now', 'mp-in');
        each(el.children, function (card) {
          if (handled(card)) return;
          if (elementIsDenied(card, section)) {
            revealNow(card);
            return;
          }
          stage(card, i);
          i += 1;
        });
        return;
      }

      stage(el, i);
      i += 1;
    });
  }

  /* Nothing the stylesheet hid may be left without .mp-in. This is the safety
     net for the two selector lists drifting apart, and for markup shapes
     neither of them anticipated. */
  function enforce(root) {
    each(safeQuery(root || document, HIDDEN_SELECTOR), function (el) {
      if (!handled(el)) revealNow(el);
    });
  }

  function scan(root) {
    each((root || document).querySelectorAll('.shopify-section'), scanSection);
    enforce(root);
  }

  function revealEverything(root) {
    each(safeQuery(root || document, HIDDEN_SELECTOR), revealNow);
    each((root || document).querySelectorAll('[data-mp-reveal]'), revealNow);
  }

  function run(root) {
    if (reduce.matches || !('IntersectionObserver' in window)) {
      revealEverything(root);
      return;
    }
    scan(root);
  }

  /* ---------- late content ----------
     Several sections render placeholders and swap in the real markup once a
     fetch returns — the related-products rail is the obvious one. Those cards
     never existed when the first scan ran, so they would arrive unstaged and
     simply appear. Watching for added elements catches them, and quick-add,
     filtered collection grids and paginated rails besides.

     Only childList is observed. This script's own work is attributes and
     classes, so it cannot retrigger itself. */
  var pending = false;

  function scheduleRescan() {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      run();
    });
  }

  function watchForLateContent() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          if (added[j].nodeType === 1) {
            scheduleRescan();
            return;
          }
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    run();
    watchForLateContent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Honour the setting being turned on after load, not only at first paint.
  var onReduceChange = function () {
    if (reduce.matches) revealEverything();
  };
  if (reduce.addEventListener) reduce.addEventListener('change', onReduceChange);
  else if (reduce.addListener) reduce.addListener(onReduceChange);

  /* The theme editor re-renders a section in place without scrolling it back
     into view, so anything already past the fold would otherwise stay hidden.
     Reveal it outright rather than re-observing. */
  document.addEventListener('shopify:section:load', function (e) {
    run(e.target);
    window.requestAnimationFrame(function () {
      revealEverything(e.target);
    });
  });
})();
